import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Database, Search, AlertTriangle, Trash2, Loader2, CheckCircle2, Info, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBrokerCleanupTab() {
  const [ibs, setIbs] = useState([]);
  const [selectedIb, setSelectedIb] = useState('');
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Selection: { 'YYYY-MM__brokerName': true }
  const [selected, setSelected] = useState({});

  // Dedup state
  const [dedupRunning, setDedupRunning] = useState(false);
  const [dedupResult, setDedupResult] = useState(null);

  const runDedup = async (dryRun) => {
    setDedupRunning(true);
    setDedupResult(null);
    try {
      const token = localStorage.getItem('token');
      const body = { dry_run: dryRun, keep_strategy: 'richest' };
      if (selectedIb) body.ib_id = selectedIb;
      const res = await axios.post(`${API}/admin/daily-data/dedup-cleanup`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDedupResult(res.data);
      const n = res.data.total_duplicate_entries_removed;
      if (dryRun) {
        toast.info(`Scan: ${n} duplicate entries dijumpai across ${res.data.modified_docs} docs`);
      } else {
        toast.success(`Cleanup: ${n} duplicates dipadam dari ${res.data.modified_docs} docs`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dedup failed (must be workspace owner)');
    } finally {
      setDedupRunning(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/ib/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setIbs((res.data?.ibs || res.data || []).map(ib => ({ id: ib.id, name: ib.name }))))
      .catch(() => {});
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/broker-cleanup/history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(res.data.history || []);
      setShowHistory(true);
    } catch (e) {
      toast.error('Failed to fetch history');
    }
  };

  const restoreBatch = async (log) => {
    if (!log.backup_available) {
      toast.error('No backup available for this cleanup (legacy cleanup before backup feature)');
      return;
    }
    const confirmMsg = `Restore ${log.modified_entries} entries for ${log.ib_name}?\n\n` +
      `Months: ${(log.affected_months || []).join(', ')}\n` +
      `Brokers removed: ${(log.remove_brokers || []).join(', ') || '(none)'}\n` +
      `Done by: ${log.actor}\nAt: ${log.at}\n\n` +
      `Tindakan ini akan REPLACE sales data dengan backup snapshot.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/broker-restore/${log.batch_id}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Restored ${res.data.restored_months.length} months`);
      fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  const runDiagnostic = async () => {
    if (!selectedIb) { toast.error('Pilih IB dulu'); return; }
    setLoading(true);
    setSelected({});
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/broker-diagnostic/${selectedIb}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDiagnostic(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnostic failed');
    } finally { setLoading(false); }
  };

  const toggleSelect = (month, brokerName) => {
    const key = `${month}__${brokerName}`;
    setSelected(s => ({ ...s, [key]: !s[key] }));
  };

  const selectAllOrphans = () => {
    const next = {};
    diagnostic?.months?.forEach(m => {
      m.brokers.forEach(b => {
        if (b.is_orphan) next[`${m.month}__${b.broker_name}`] = true;
      });
    });
    setSelected(next);
  };

  const selectAllLegacy = () => {
    const next = {};
    diagnostic?.months?.forEach(m => {
      m.brokers.forEach(b => {
        if (b.is_legacy) next[`${m.month}__${b.broker_name}`] = true;
      });
    });
    setSelected(next);
  };

  const clearSelection = () => setSelected({});

  // Build payload grouped by month
  const buildPayload = () => {
    const map = {};   // month -> { remove_brokers:[], remove_legacy_fields:[] }
    Object.keys(selected).forEach(k => {
      if (!selected[k]) return;
      const [month, ...rest] = k.split('__');
      const broker = rest.join('__');
      if (!map[month]) map[month] = { remove_brokers: [], remove_legacy_fields: [] };
      if (broker.startsWith('[legacy ')) {
        const legacyKey = broker.replace('[legacy ', '').replace(']', '');
        map[month].remove_legacy_fields.push(legacyKey);
      } else {
        map[month].remove_brokers.push(broker);
      }
    });
    return map;
  };

  const runCleanup = async () => {
    const map = buildPayload();
    const months = Object.keys(map);
    if (months.length === 0) { toast.error('Pilih sekurang-kurangnya 1 broker untuk remove'); return; }

    // Aggregate unique brokers & legacy fields across all selected months
    const remove_brokers = Array.from(new Set(months.flatMap(m => map[m].remove_brokers)));
    const remove_legacy_fields = Array.from(new Set(months.flatMap(m => map[m].remove_legacy_fields)));

    const ibName = diagnostic?.ib_name || selectedIb;
    const confirmMsg = `Confirm remove dari ${ibName}:\n\n` +
      `Months: ${months.join(', ')}\n` +
      `Brokers: ${remove_brokers.join(', ') || '(none)'}\n` +
      `Legacy fields: ${remove_legacy_fields.join(', ') || '(none)'}\n\n` +
      `Tindakan ini TIDAK boleh undo. Teruskan?`;
    if (!window.confirm(confirmMsg)) return;

    setCleaning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/broker-cleanup/${selectedIb}`, {
        months,
        remove_brokers,
        remove_legacy_fields,
      }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      toast.success(`Cleanup done — ${res.data.modified_entries} entries modified across ${res.data.affected_months.length} months`);
      // Re-run diagnostic
      await runDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally { setCleaning(false); }
  };

  const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-5 rounded-xl bg-gradient-to-r from-[#E63946]/15 to-transparent border border-[#E63946]/30">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-[#E63946]/20 border border-[#E63946]/40 flex items-center justify-center">
            <Database size={22} className="text-[#E63946]" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Broker Data Cleanup</h3>
            <p className="text-xs text-[hsl(var(--text-muted))]">Diagnose & remove orphan broker entries dari daily_data_2026 sales — scoped per IB</p>
          </div>
        </div>
        <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-[#EF4444]/15 border border-[#EF4444]/40">
          <AlertTriangle size={16} className="text-[#EF4444] mt-0.5 shrink-0" />
          <div className="text-[11px] text-[#FCA5A5] space-y-1">
            <p><strong className="text-white">⚠️ CRITICAL — Read before using:</strong></p>
            <p>• Tool ini buang data broker secara KEKAL dari daily_data_2026 sales entries.</p>
            <p>• Cuma tick broker yang ditandakan <Badge className="text-[8px] bg-red-500/20 text-red-400 border-red-500/30 mx-1">ORPHAN</Badge> atau <Badge className="text-[8px] bg-purple-500/20 text-purple-400 border-purple-500/30 mx-1">LEGACY</Badge> — JANGAN tick yang <Badge className="text-[8px] bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mx-1">ENABLED</Badge>.</p>
            <p>• Kalau &quot;ORPHAN&quot; broker rupanya data sebenar (case mismatch dgn config), <strong>JANGAN remove</strong> — pergi ke Channel Settings → Broker Config dan add broker tu ke enabled list dulu, baru data tally.</p>
            <p>• <strong className="text-[#10B981]">Auto-backup ENABLED</strong>: Setiap cleanup save full snapshot. Boleh restore via History button di bawah.</p>
          </div>
        </div>
      </div>

      {/* IB Picker */}
      <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center gap-3 flex-wrap">
        <label className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--text-muted))]">IB:</label>
        <Select value={selectedIb} onValueChange={setSelectedIb}>
          <SelectTrigger className="w-[260px] h-9 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="cleanup-ib-selector">
            <SelectValue placeholder="Pilih IB" />
          </SelectTrigger>
          <SelectContent>
            {ibs.map(ib => <SelectItem key={ib.id} value={ib.id}>{ib.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={runDiagnostic} disabled={!selectedIb || loading} className="text-xs bg-[#3B82F6] hover:bg-[#2563EB]" data-testid="run-diagnostic-btn">
          {loading ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Search size={12} className="mr-1.5" />}
          Run Diagnostic
        </Button>
        <Button size="sm" variant="outline" onClick={fetchHistory} className="text-xs ml-auto" data-testid="cleanup-history-btn">
          <CheckCircle2 size={12} className="mr-1.5 text-emerald-400" />
          Cleanup History & Restore
        </Button>
      </div>

      {/* Cleanup History */}
      {showHistory && (
        <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))]" data-testid="history-section">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-white">Cleanup History (Last 50)</h4>
            <Button size="sm" variant="ghost" onClick={() => setShowHistory(false)} className="text-xs">Hide</Button>
          </div>
          {history.length === 0 ? (
            <p className="text-[11px] text-[hsl(var(--text-muted))] text-center py-4">No cleanup history yet.</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {history.map((log, i) => (
                <div key={i} className="p-3 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]" data-testid={`history-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-white">{log.ib_name || log.ib_id}</span>
                        <Badge className="text-[9px] bg-red-500/15 text-red-400 border-red-500/30">{log.modified_entries} entries removed</Badge>
                        {log.backup_available
                          ? <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Backup ✓</Badge>
                          : <Badge className="text-[9px] bg-gray-500/15 text-gray-400 border-gray-500/30">No backup (legacy)</Badge>}
                      </div>
                      <p className="text-[10px] text-[hsl(var(--text-muted))]">
                        Months: <span className="text-white">{(log.affected_months || log.months || []).join(', ')}</span>
                      </p>
                      <p className="text-[10px] text-[hsl(var(--text-muted))]">
                        Brokers: <span className="text-white">{[...(log.remove_brokers || []), ...((log.remove_legacy || []).map(l => `[legacy ${l}]`))].join(', ') || '(none)'}</span>
                      </p>
                      <p className="text-[10px] text-[hsl(var(--text-muted))]">
                        By: <span className="text-white">{log.actor}</span> • {new Date(log.at).toLocaleString()}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => restoreBatch(log)} disabled={!log.backup_available}
                      className="text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40" data-testid={`restore-${i}`}>
                      Restore
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Diagnostic Results */}
      {diagnostic && (
        <>
          <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <p className="text-xs text-[hsl(var(--text-muted))]">IB</p>
                <p className="text-sm font-bold text-white">{diagnostic.ib_name}</p>
              </div>
              <div>
                <p className="text-xs text-[hsl(var(--text-muted))]">Enabled brokers (display name)</p>
                <p className="text-sm font-mono text-emerald-400">{(diagnostic.enabled_display_names || []).filter(n => !n.startsWith('broker')).join(', ') || '—'}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={selectAllOrphans} className="text-xs">Select all ORPHAN</Button>
                <Button size="sm" variant="outline" onClick={selectAllLegacy} className="text-xs">Select all LEGACY</Button>
                <Button size="sm" variant="outline" onClick={clearSelection} className="text-xs">Clear</Button>
              </div>
            </div>

            {(diagnostic.months || []).map(m => (
              <div key={m.month} className="mb-4 rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                <div className="px-4 py-2.5 bg-[hsl(var(--surface-elevated))] border-b border-[hsl(var(--border))] flex items-center justify-between flex-wrap gap-2">
                  <span className="text-sm font-bold text-white">{m.month}</span>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="text-[hsl(var(--text-muted))]">Total: <span className="text-white font-mono">${fmt(m.total_commission_usd)}</span></span>
                    <span className="text-emerald-400">Enabled: <span className="font-mono">${fmt(m.enabled_commission_usd)}</span></span>
                    {m.orphan_commission_usd > 0 && <span className="text-red-400">Orphan: <span className="font-mono">${fmt(m.orphan_commission_usd)}</span></span>}
                    {m.legacy_commission_usd > 0 && <span className="text-purple-400">Legacy: <span className="font-mono">${fmt(m.legacy_commission_usd)}</span></span>}
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--surface))]">
                    <tr className="border-b border-[hsl(var(--border))]">
                      <th className="px-3 py-2 text-left w-10"></th>
                      <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Broker</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">USD</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">~MYR</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Lots</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Active</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Entries</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.brokers.map(b => {
                      const key = `${m.month}__${b.broker_name}`;
                      const checked = !!selected[key];
                      const canRemove = b.is_orphan || b.is_legacy;
                      return (
                        <tr key={key} className={`border-b border-[hsl(var(--border))]/40 ${checked ? 'bg-red-500/8' : 'hover:bg-[hsl(var(--surface-elevated))]/40'}`}>
                          <td className="px-3 py-2 text-center">
                            {canRemove ? (
                              <input type="checkbox" checked={checked}
                                onChange={() => toggleSelect(m.month, b.broker_name)}
                                className="cursor-pointer"
                                data-testid={`cb-${m.month}-${b.broker_name}`} />
                            ) : <span className="text-emerald-500">✓</span>}
                          </td>
                          <td className="px-3 py-2 font-semibold text-white">{b.broker_name}</td>
                          <td className="px-3 py-2 text-right font-mono">${fmt(b.commission_usd)}</td>
                          <td className="px-3 py-2 text-right font-mono text-[hsl(var(--text-muted))]">RM {fmt(b.commission_myr_approx)}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmt(b.lots)}</td>
                          <td className="px-3 py-2 text-right font-mono">{b.active}</td>
                          <td className="px-3 py-2 text-right font-mono text-[hsl(var(--text-muted))]">{b.entry_count}</td>
                          <td className="px-3 py-2 text-center">
                            {b.is_enabled && <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30">ENABLED</Badge>}
                            {b.is_orphan && <Badge className="text-[8px] bg-red-500/15 text-red-400 border-red-500/30">ORPHAN</Badge>}
                            {b.is_legacy && <Badge className="text-[8px] bg-purple-500/15 text-purple-400 border-purple-500/30">LEGACY</Badge>}
                          </td>
                        </tr>
                      );
                    })}
                    {m.brokers.length === 0 && (
                      <tr><td colSpan={8} className="px-3 py-4 text-center text-[hsl(var(--text-muted))] text-xs">No broker data for this month</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}

            {/* Cleanup button */}
            <div className="flex items-center justify-end mt-4 gap-2">
              <span className="text-[11px] text-[hsl(var(--text-muted))]">
                Selected: <strong className="text-white">{Object.values(selected).filter(Boolean).length}</strong> broker entries
              </span>
              <Button onClick={runCleanup} disabled={cleaning || Object.values(selected).filter(Boolean).length === 0}
                className="bg-[#E63946] hover:bg-[#c62828] text-xs" data-testid="run-cleanup-btn">
                {cleaning ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Trash2 size={12} className="mr-1.5" />}
                Remove Selected
              </Button>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-[#3B82F6]/10 border border-[#3B82F6]/30 flex items-start gap-2">
            <Info size={14} className="text-[#3B82F6] mt-0.5 shrink-0" />
            <p className="text-[11px] text-[#93C5FD]">
              Lepas cleanup, refresh page Yearly Snapshot / Overall Performance / War Room untuk tengok new totals.
              Audit log saved ke <code className="text-white">security_logs</code> collection.
            </p>
          </div>
        </>
      )}

      {/* ──────────────  DAILY DATA DEDUP CLEANUP ────────────── */}
      <div className="mt-6 pt-6 border-t border-[hsl(var(--border))]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-1">
              <Layers size={15} className="text-amber-400" />
              Daily Data Dedup Cleanup
            </h3>
            <p className="text-[11px] text-[hsl(var(--text-muted))] max-w-2xl leading-relaxed">
              Scan & padam duplicate entries (same date) dari <code className="text-white">daily_data_2026</code>.
              Punca utama inflated commission/lot di Yearly Snapshot, Overall Performance, Channel Overview.
              Default: keep entry paling lengkap (richest data) per tarikh.
            </p>
            {selectedIb && (
              <p className="text-[10px] text-amber-400 mt-1">
                Scoped to: <span className="font-mono">{ibs.find(x => x.id === selectedIb)?.name}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={() => runDedup(true)} disabled={dedupRunning} variant="outline" size="sm" data-testid="dedup-scan-btn">
              {dedupRunning ? <Loader2 size={12} className="animate-spin mr-1" /> : <Search size={12} className="mr-1" />}
              Scan (Dry Run)
            </Button>
            <Button onClick={() => {
              if (window.confirm('Run REAL cleanup? Duplicates will be permanently removed from DB. (Owner only.)')) {
                runDedup(false);
              }
            }} disabled={dedupRunning || !dedupResult || dedupResult.total_duplicate_entries_removed === 0}
              className="bg-red-600 hover:bg-red-700" size="sm" data-testid="dedup-run-btn">
              <Trash2 size={12} className="mr-1" />
              Run Cleanup
            </Button>
          </div>
        </div>

        {dedupResult && (
          <div className="rounded-lg border border-[hsl(var(--border))] p-3 bg-[hsl(var(--surface))]">
            <div className="flex items-center gap-3 mb-2 text-[12px] flex-wrap">
              <Badge className={dedupResult.dry_run ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'}>
                {dedupResult.dry_run ? 'DRY RUN' : 'COMMITTED'}
              </Badge>
              <span className="text-white font-bold">{dedupResult.total_duplicate_entries_removed}</span>
              <span className="text-[hsl(var(--text-muted))]">in-array dupes across</span>
              <span className="text-white font-bold">{dedupResult.modified_docs}</span>
              <span className="text-[hsl(var(--text-muted))]">docs</span>
              {dedupResult.duplicate_docs_removed > 0 && (
                <>
                  <span className="text-[hsl(var(--text-muted))]">·</span>
                  <Badge className="bg-red-500/15 text-red-400 border-red-500/30">
                    {dedupResult.duplicate_docs_removed} duplicate DOCUMENTS detected
                  </Badge>
                </>
              )}
            </div>

            {/* Duplicate documents section */}
            {dedupResult.duplicate_docs_summary && dedupResult.duplicate_docs_summary.length > 0 && (
              <div className="mb-3 rounded-md border border-red-500/30 bg-red-500/5 p-2">
                <p className="text-[11px] font-bold text-red-400 mb-1.5">
                  ⚠️ Duplicate DOCUMENTS (root cause of 2× / 3× inflation):
                </p>
                <table className="w-full text-[11px]">
                  <thead className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">
                    <tr>
                      <th className="text-left py-1">IB</th>
                      <th className="text-left py-1">Month</th>
                      <th className="text-right py-1">Extra Docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dedupResult.duplicate_docs_summary.map((s, i) => (
                      <tr key={i} className="border-t border-red-500/20">
                        <td className="py-1 font-mono text-white">{ibs.find(x => x.id === s.ib_id)?.name || s.ib_id}</td>
                        <td className="py-1">{s.month}</td>
                        <td className="py-1 text-right font-mono text-red-400 font-bold">+{s.duplicate_docs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {dedupResult.summary && dedupResult.summary.length > 0 && (
              <div className="overflow-x-auto max-h-[280px] overflow-y-auto rounded-md border border-[hsl(var(--border))]">
                <table className="w-full text-[11px]">
                  <thead className="bg-[hsl(var(--card))] text-[hsl(var(--text-muted))] text-[10px] uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-1.5">IB</th>
                      <th className="text-left px-3 py-1.5">Month</th>
                      <th className="text-right px-3 py-1.5">Marketing</th>
                      <th className="text-right px-3 py-1.5">Sales</th>
                      <th className="text-right px-3 py-1.5">Operation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dedupResult.summary.map((s, i) => (
                      <tr key={i} className="border-t border-[hsl(var(--border))]">
                        <td className="px-3 py-1.5 font-mono text-white">{ibs.find(x => x.id === s.ib_id)?.name || s.ib_id}</td>
                        <td className="px-3 py-1.5">{s.month}</td>
                        <td className="px-3 py-1.5 text-right font-mono" style={{color: s.marketing_removed > 0 ? '#f59e0b' : 'inherit'}}>{s.marketing_removed}</td>
                        <td className="px-3 py-1.5 text-right font-mono" style={{color: s.sales_removed > 0 ? '#f59e0b' : 'inherit'}}>{s.sales_removed}</td>
                        <td className="px-3 py-1.5 text-right font-mono" style={{color: s.operation_removed > 0 ? '#f59e0b' : 'inherit'}}>{s.operation_removed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
